import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { addToOrder, getSeatLayout, clearOrder } from "../../../redux/slices";

import { Spinner } from "../../UI/Spinner";
import { STATUS } from "../../../utils/constants";
import Sample from "./sample";
import { useTranslation } from "react-i18next";

//solid glyphs rather than thin strokes, they stay legible at the seat button size
const BedIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    {/* headboard, foot post, mattress and pillow */}
    <rect x="1.5" y="4" width="3" height="16" rx="1" />
    <rect x="19.5" y="13" width="3" height="7" rx="1" />
    <rect x="1.5" y="12" width="21" height="5" rx="1.5" />
    <rect x="6" y="7" width="6.5" height="4.5" rx="1.5" />
  </svg>
);

//a bed that occupies two columns: same solid style in a box twice as wide,
//with the two pillows that mark it as a double
const WideBedIcon = (props) => (
  <svg viewBox="0 0 48 24" fill="currentColor" aria-hidden="true" {...props}>
    {/* headboard, foot post, mattress and two pillows */}
    <rect x="1.5" y="4" width="3" height="16" rx="1" />
    <rect x="43.5" y="13" width="3" height="7" rx="1" />
    <rect x="1.5" y="12" width="45" height="5" rx="1.5" />
    <rect x="6" y="7" width="6.5" height="4.5" rx="1.5" />
    <rect x="14.5" y="7" width="6.5" height="4.5" rx="1.5" />
  </svg>
);

const SofaIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    {/* backrest, two armrests and the seat cushion */}
    <rect x="4.5" y="4" width="15" height="7" rx="2" />
    <rect x="1" y="8.5" width="4" height="9" rx="2" />
    <rect x="19" y="8.5" width="4" height="9" rx="2" />
    <rect x="4" y="11.5" width="16" height="6" rx="1.5" />
    <rect x="3" y="17" width="2.5" height="3" rx="1" />
    <rect x="18.5" y="17" width="2.5" height="3" rx="1" />
  </svg>
);

const seatTypeIcon = (seatType, span = 1) => {
  switch (String(seatType || "").toLowerCase()) {
    case "bed":
      //an unpaired bed keeps the single-column glyph rather than overflowing
      return span === 2 ? WideBedIcon : BedIcon;
    case "sofa":
      return SofaIcon;
    default:
      //regular, null or any unknown type falls back to the plain text seat
      return null;
  }
};

/* ------------------------------------------------------------------ *
 * Seat spans
 *
 * A bed is physically twice as wide as any other seat, so it occupies two
 * columns. The API has no span field, so a bed arrives as two consecutive
 * seats in the same row:
 *
 *   head -> status: <the configured status>, seatType: "Bed", seatNumber: <real>
 *   tail -> status: "Aisle",                 seatType: "Bed", seatNumber: 0
 *
 * A genuine aisle has no seat type, which is what tells the two apart. Rows
 * arrive already ordered, so adjacency in the array is enough - there is no
 * columnNumber in this payload.
 * ------------------------------------------------------------------ */

const isSeatType = (seat, type) =>
  String(seat?.seatType || "").toLowerCase() === type;

const isAisle = (seat) => seat?.status === "Aisle";

/** The absorbed second column of a bed - never rendered on its own. */
const isBedTail = (seat) => isAisle(seat) && isSeatType(seat, "bed");

/** The visible half of a bed. */
const isBedHead = (seat) => isSeatType(seat, "bed") && !isAisle(seat);

/**
 * One cell per rendered unit, in row order, with a bed's absorbed column
 * folded into its head. A bed with no tail (last column, or corrupted data)
 * falls back to a single-width cell rather than overflowing the row.
 */
const buildRowCells = (row = []) => {
  const cells = [];
  for (let i = 0; i < row.length; i++) {
    // Tails are drawn as part of the head that precedes them.
    if (isBedTail(row[i]) && isBedHead(row[i - 1])) continue;

    const paired = isBedHead(row[i]) && isBedTail(row[i + 1]);
    cells.push({ seat: row[i], span: paired ? 2 : 1 });
    if (paired) i++;
  }
  return cells;
};

const SeatAllocationModal = ({
  allocationData,
  closeFunc,
  addTicketToOrder,
}) => {
  const { t } = useTranslation(["Home", "Common"]);
  const dispatch = useDispatch();
  const { seatLayout, seatLayoutErr, seatLayoutStatus } = useSelector(
    (state) => state.showtimes,
  );

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  //comment to push again

  const formatPkgTktPayload = () => {
    const data = allocationData.pricecard;
    const x = allocationData.showtime;

    return {
      id: data.ticketId,
      availableQuantity: x.totalSeats - x.seatsSold,
      name: x.film,
      price: 0,
      priceInPackage: data?.ticketPackage?.tickets[0]?.price,
      totalPackagePrice: data.price,
      canModify: false,
      showtimeId: x.id,
      packageTicketId: data.ticketId,
      ticketId: data.ticketId,
      //using this base quantity to preserve the quantity of the package so if its an item in the package has a quantity of
      // 2 when the package is added to the cart again it is incremented by 2 not the default one
      baseQuantity: 1,
      orderQuantity: 1,
      //this id would serve as a unique identifier for all the children of a package ticket so if any of them gets deleted
      //the siblings would be deleted too
      idForDelete: data.id,
      priceCardTicketId: data.id,
      screen: x.screen || t("ticket.empty.screen"),
      startTime: x.startTime,
      showtimeDate: new Date(x.startTime).toDateString(),
      time:
        new Date(x.startTime).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }) +
        "-" +
        new Date(x.endTime).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
    };
  };

  useEffect(() => {
    // if (
    //   allocationData?.showtime?.id &&
    //   allocationData?.pricecard?.seatClassId
    // ) {
    dispatch(
      getSeatLayout({
        showtimeId: allocationData?.showtime?.id,
        seatClassId: allocationData?.pricecard?.seatClassId,
      }),
    );
    // }
  }, [allocationData]);

  // const sampleSeats = [
  //   "A",
  //   "B",
  //   "C",
  //   "D",
  //   "E",
  //   "F",
  //   "G",
  //   "H",
  //   "I",
  //   "J",
  //   "K",
  //   "L",
  // ];

  // const newSample = sampleSeats
  //   .map((q) => {
  //     return Array.from({ length: 25 }, (x, i) => {
  //       return {
  //         seat: q + (i + 1),
  //         status: q === "K" || q == "L" ? 0 : Math.floor(Math.random() * 2) + 1,
  //       };
  //     });
  //   })
  //   .reverse();

  //available 0
  //reserved 1
  // unavailable 2

  //tickets covered by the selection - a bed counts as two
  const allocatedQty = selectedSeats.reduce((n, s) => n + s.ticketQty, 0);

  //span is how many columns the seat covers, and so how many tickets it takes:
  //a paired bed is two, everything else (an unpaired bed included) is one.
  //the seat goes into the order as a single line with that quantity
  const selectFunc = (data, span = 1) => {
    if (data.status === "Available") {
      let updatedSelectedSeats = [...selectedSeats];
      let identifier = data?.name + data?.seatNumber;
      let isSelected = selectedSeats.find((x) => {
        return x.identifier === identifier;
      });
      if (isSelected) {
        updatedSelectedSeats = selectedSeats.filter((x) => {
          return x.identifier !== identifier;
        });
        setSelectedSeats(updatedSelectedSeats);
        setErrorMsg("");
      } else {
        //a bed takes two tickets, every other seat takes one
        const ticketQty = span;
        const qtyLeft = (allocationData?.qtyOfSeats || 0) - allocatedQty;
        if (ticketQty <= qtyLeft) {
          updatedSelectedSeats.push({
            seatNumber: data.seatNumber,
            identifier,
            seatLayoutId: data.id,
            rowName: data.name,
            ticketQty,
          });
          setSelectedSeats(updatedSelectedSeats);
          setErrorMsg("");
        } else if (qtyLeft > 0) {
          //some tickets are left, just not enough to cover a bed
          setErrorMsg(t("ticket.seat.error.bed"));
        }
      }
    }
  };

  const submitFunc = () => {
    if (allocatedQty < allocationData?.qtyOfSeats) {
      setErrorMsg(t("ticket.seat.error.select"));
      return;
    }
    setErrorMsg("");
    if (allocationData?.pricecard?.ticketGroup === "Standard") {
      addTicketToOrder({
        pricecard: allocationData?.pricecard,
        item: allocationData?.showtime,
        selectedSeats,
      });
      setSelectedSeats([]);
      closeFunc();
    }
    if (allocationData?.pricecard?.ticketGroup === "Package") {
      const formattedPkgData = formatPkgTktPayload();

      selectedSeats.forEach((seat) => {
        dispatch(
          addToOrder({
            ...formattedPkgData,
            //a bed is one line covering both of its tickets
            orderQuantity: seat.ticketQty,
            seatLayoutId: seat.seatLayoutId,
            seatNumber: seat.seatNumber,
            seatIdentifier: seat.identifier,
            rowName: seat.rowName,
          }),
        );
      });
      setSelectedSeats([]);
      closeFunc();
    }
  };

  return (
    <Transition appear show={!!allocationData} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 "
        onClose={() => {
          // setShowError(false);
          // setEnteredPin("");
          // closeFunc();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className=" overflow-auto  transform  rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* <Dialog.Title
                  as="h1"
                  className="text-lg font-medium leading-6 bold text-gray-900 text-center"
                >
                  Seat Allocation
                </Dialog.Title> */}
                <div className="flex justify-center py-2">
                  {" "}
                  <Spinner status={seatLayoutStatus} />{" "}
                </div>
                <div className=" flex justify-center mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      closeFunc();
                      dispatch(clearOrder());
                    }}
                    className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-red-700 px-4 py-2 text-sm font-medium text-white mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    {t("Common:button.cancel")}
                  </button>
                  <button
                    type="button"
                    className=" mr-2 inline-flex items-center justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={submitFunc}
                  >
                    <span>{t("Common:button.submit")} </span>
                  </button>
                </div>
                {errorMsg && (
                  <p className="text-red-500 text-center"> {errorMsg}</p>
                )}
                {seatLayoutErr && (
                  <p className="text-red-500 text-center"> {seatLayoutErr}</p>
                )}
                {seatLayoutStatus === STATUS.RESOLVED &&
                  seatLayout?.length < 1 && (
                    <p className="text-red-500 text-center max-w-md mx-auto">
                      {t("ticket.seat.error.load")}
                    </p>
                  )}
                <div className="flex justify-between items-center">
                  <div className="flex justify-start text-xs mb-2">
                    <div className="flex items-center">
                      <div className="bg-primary w-3 h-3 mr-1"></div>
                      <span className="mr-1">{t("ticket.seat.status.a")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-gray-500 w-3 h-3 mr-1"></div>
                      <span className="mr-1">{t("ticket.seat.status.r")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-gray-800 w-3 h-3 mr-1"></div>
                      <span className="mr-1">{t("ticket.seat.status.u")}</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-500 w-3 h-3 mr-1"></div>
                      <span className="mr-1">{t("ticket.seat.status.s")}</span>
                    </div>
                  </div>
                  <div className="flex justify-start text-xs mb-2 text-gray-700">
                    <div className="flex items-center mr-2">
                      <WideBedIcon className="w-8 h-4 mr-1" />
                      <span>{t("ticket.seat.type.bed")}</span>
                    </div>
                    <div className="flex items-center">
                      <SofaIcon className="w-4 h-4 mr-1" />
                      <span>{t("ticket.seat.type.sofa")}</span>
                    </div>
                  </div>
                </div>
                <p className="text-center font-bold">
                  {t("ticket.seat.count.selected", {
                    count: allocatedQty,
                    total: allocationData?.qtyOfSeats || 0,
                  })}
                </p>
                {seatLayoutStatus === STATUS.RESOLVED && (
                  <div>
                    <div className=" flex justify-center pt-2">
                      {" "}
                      <div className="bg-primary p-1 text-white rounded text-xs">
                        {" "}
                        {t("ticket.seat.title")}
                      </div>
                    </div>
                    {[...seatLayout]?.map((x, index) => {
                      return (
                        <div
                          key={index}
                          className=" my-1 flex justify-center items-end gap-1"
                        >
                          {buildRowCells(x).map(({ seat: y, span }, cellIdx) => {
                            const Icon = seatTypeIcon(y?.seatType, span);
                            return (
                              <button
                                disabled={
                                  y.status === "Unavailable" ||
                                  y.status === "Aisle" ||
                                  y.status === "Reserved"
                                }
                                key={index + "-" + cellIdx}
                                //a bed covers two columns plus the gap between
                                //them, so it stays flush with the rest of the grid
                                className={`shrink-0 ${
                                  span === 2 ? "w-[4.25rem]" : "w-8"
                                } disabled:cursor-not-allowed text-white text-sm rounded ${
                                  y.status === "Unavailable"
                                    ? "bg-gray-800"
                                    : y.status === "Reserved"
                                    ? "bg-gray-500"
                                    : y.status === "Available"
                                    ? "bg-primary"
                                    : ""
                                } ${
                                  selectedSeats
                                    .map((x) => {
                                      return x.identifier;
                                    })
                                    .includes(y?.name + y?.seatNumber)
                                    ? "bg-blue-500"
                                    : ""
                                }`}
                                onClick={() => {
                                  selectFunc(y, span);
                                }}
                              >
                                {Icon ? (
                                  <span className="flex flex-col items-center leading-tight">
                                    <Icon
                                      className={
                                        span === 2 ? "w-8 h-4" : "w-4 h-4"
                                      }
                                    />
                                    <span>{y?.name + y?.seatNumber}</span>
                                  </span>
                                ) : (
                                  <> {y?.name + y?.seatNumber}</>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* <Sample /> */}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
export default SeatAllocationModal;
